import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import { omit, lowerCase } from 'lodash'

import { useUserContext } from '../../../auth/UserProvider'
import {
  INCIDENT_CODE_INCORRECT_ADDRESS,
  INCIDENT_CODE_NO_ANSWER,
  INCIDENT_CODE_NO_ANSWER_R,
  INCIDENT_CODE_NO_ANSWER_RE,
  INCIDENT_CODE_NO_EFFECTIVE,
  INCIDENT_CODE_REFUSED,
  START_DATE_QUARTER_DATA,
  TODAY_DATE,
} from '../../../common/constants'
import useTranslations from '../../../i18n/useTranslations'

import FilteringChipsView from './View'

const NUMBER_OF_MANDATORY_FILTERS = 1

const FILTER_KEYS_ORDER = [
  // NOTE: search input
  'search',
  // NOTE: filters
  'serviceType',
  'postalCode',
  'originPostalCode',
  'destinPostalCode',
  'clientCenterCode',
  'country',
  'date',
  'statusCodes',
  'incidentCodes',
]

const DEFAULT_FILTERS_VALUES = {
  defaultDateFrom: START_DATE_QUARTER_DATA,
  defaultDateTo: TODAY_DATE,
}

const useFiltersChipProperties = (
  filters,
  disabledStatusCodeFilters,
  updateFilters,
) => {
  const t = useTranslations()
  const { statusList, centres } = useUserContext()
  const removeFiltersByName = useCallback(
    (...filterNames) => {
      updateFilters(omit(filters, filterNames))
    },
    [filters, updateFilters],
  )
  const removeValueFromFilter = useCallback(
    (filterKey, valueToRemove) => {
      const newFilters = {
        ...filters,
        [filterKey]: filters[filterKey].filter(
          value => value !== valueToRemove,
        ),
      }
      updateFilters(newFilters)
    },
    [filters, updateFilters],
  )

  return useMemo(() => {
    const SKIP_FILTER = []

    const hasDefaultDateFilter =
      filters.dateFrom === DEFAULT_FILTERS_VALUES.defaultDateFrom &&
      filters.dateTo === DEFAULT_FILTERS_VALUES.defaultDateTo

    const sortedFilterKeys = hasDefaultDateFilter
      ? ['date', ...FILTER_KEYS_ORDER.filter(key => key !== 'date')]
      : FILTER_KEYS_ORDER

    return sortedFilterKeys.flatMap(filterKey => {
      switch (filterKey) {
        case 'serviceType': {
          if (!filters.serviceType) return SKIP_FILTER

          return {
            key: filterKey,
            labelTitle: filters.serviceType.split('-')[0],
            labelDescription: t('shipping-tracking-chip:service-type'),
            deleteFilter: () => removeFiltersByName('serviceType'),
          }
        }

        case 'search': {
          if (!filters.search) return SKIP_FILTER

          return {
            key: filterKey,
            labelTitle: filters.search,
            labelDescription: t(
              'shipping-tracking-chip:shipping-code-or-reference',
            ),
            deleteFilter: () => removeFiltersByName('search'),
          }
        }

        // NOTE: agrupa 'postalCodeFrom', 'postalCodeTo' y 'postalCodeSource'
        case 'postalCode': {
          if (!filters.postalCodeTo || !filters.postalCodeSource)
            return SKIP_FILTER

          const postaCodeTxt = !filters.postalCodeTo
            ? filters.postalCodeFrom
            : `${filters.postalCodeFrom} - ${filters.postalCodeTo}`

          return {
            key: filterKey,
            labelTitle: postaCodeTxt,
            labelDescription: `${t(
              'shipping-tracking-chip:postal-code',
            )} ${lowerCase(t(`noun:${filters.postalCodeSource}`))}`,
            deleteFilter: () =>
              removeFiltersByName('postalCodeFrom', 'postalCodeTo'),
          }
        }
        case 'originPostalCode': {
          if (!filters.originPostalCode) return SKIP_FILTER

          return {
            key: filterKey,
            labelTitle: filters.originPostalCode,
            labelDescription: t('shipping-tracking-chip:origin-postal-code'),
            deleteFilter: () => removeFiltersByName('originPostalCode'),
          }
        }
        case 'destinPostalCode': {
          if (!filters.destinPostalCode) return SKIP_FILTER

          return {
            key: filterKey,
            labelTitle: filters.destinPostalCode,
            labelDescription: t('shipping-tracking-chip:destin-postal-code'),
            deleteFilter: () => removeFiltersByName('destinPostalCode'),
          }
        }

        case 'clientCenterCode': {
          if (!filters.clientCenterCode) return SKIP_FILTER

          return {
            key: filterKey,
            labelTitle:
              centres?.find(({ hideId }) => hideId === filters.clientCenterCode)
                .id ?? '',
            labelDescription: t('shipping-tracking-chip:center-code'),
            deleteFilter: () => removeFiltersByName('clientCenterCode'),
          }
        }

        case 'country': {
          if (!filters.country) return SKIP_FILTER

          return {
            key: filterKey,
            labelTitle: filters.country,
            labelDescription: t('shipping-tracking-chip:country'),
            deleteFilter: () => removeFiltersByName('country'),
          }
        }

        // NOTE: agrupa 'dateFrom' y 'dateTo'
        case 'date': {
          if (!filters.dateFrom || !filters.dateTo) return SKIP_FILTER

          const canBeDeleted = !hasDefaultDateFilter
          const deleteFilter = () => removeFiltersByName('dateFrom', 'dateTo')

          return {
            key: 'date',
            labelDescription: `${format(
              new Date(filters.dateFrom),
              'dd/MM/yyyy',
            )} | ${format(new Date(filters.dateTo), 'dd/MM/yyyy')}`,
            deleteFilter: canBeDeleted ? deleteFilter : undefined,
          }
        }
        case 'statusCodes': {
          if (
            !filters.statusCode ||
            filters.statusCode.length === 0 ||
            disabledStatusCodeFilters
          )
            return SKIP_FILTER
          return filters.statusCode.map(statusCode => {
            const key = `${filterKey}-${statusCode}`
            const labelDescription =
              statusCode === '3000'
                ? t('tracking:cancelled')
                : statusCode === '0030'
                ? t('tracking:deposited')
                : statusCode === '0020'
                ? t('tracking:depositPending')
                : statusList?.find(({ code }) => code === statusCode)?.name

            const deleteFilter = () =>
              removeValueFromFilter('statusCode', statusCode)

            return labelDescription
              ? {
                  key,
                  labelDescription,
                  deleteFilter,
                }
              : {
                  key,
                  labelTitle: statusCode,
                  labelDescription: t('noun:shippingStatusCode'),
                  deleteFilter,
                }
          })
        }
        case 'incidentCodes': {
          if (
            !filters.incidentCode ||
            filters.incidentCode.length === 0 ||
            disabledStatusCodeFilters
          )
            return SKIP_FILTER
          return filters.incidentCode.map(incidentCode => {
            const INCIDENT_CODE_LABEL_MAP = {
              [INCIDENT_CODE_INCORRECT_ADDRESS]: 'tracking:incorrect_address',
              [INCIDENT_CODE_NO_ANSWER]: 'tracking:no_answer',
              [INCIDENT_CODE_NO_ANSWER_RE]: 'tracking:no_answer',
              [INCIDENT_CODE_REFUSED]: 'tracking:refuses',
              [INCIDENT_CODE_NO_EFFECTIVE]: 'tracking:no_effective',
            }
            const key = `${filterKey}-${incidentCode}`
            const labelDescription = t(
              `${INCIDENT_CODE_LABEL_MAP[incidentCode]}`,
            )

            const deleteFilter = () =>
              removeValueFromFilter('incidentCode', incidentCode)

            return labelDescription
              ? {
                  key,
                  labelDescription,
                  deleteFilter,
                }
              : {
                  key,
                  labelTitle: incidentCode,
                  labelDescription: t('noun:shippingStatusCode'),
                  deleteFilter,
                }
          })
        }

        default: {
          return SKIP_FILTER
        }
      }
    })
  }, [
    filters.clientCenterCode,
    filters.country,
    filters.dateFrom,
    filters.dateTo,
    filters.destinPostalCode,
    filters.originPostalCode,
    filters.postalCodeFrom,
    filters.postalCodeSource,
    filters.postalCodeTo,
    filters.search,
    filters.serviceType,
    filters.statusCode,
    disabledStatusCodeFilters,
    removeFiltersByName,
    removeValueFromFilter,
    statusList,
    t,
  ])
}

const FilteringChips = ({
  filters,
  disabledStatusCodeFilters,
  onUpdateFilters,
  onClearFilters,
}) => {
  const formattedFilters = useFiltersChipProperties(
    filters,
    disabledStatusCodeFilters,
    onUpdateFilters,
  )

  return (
    <FilteringChipsView
      filters={formattedFilters}
      handleClearFilters={
        formattedFilters.length !== NUMBER_OF_MANDATORY_FILTERS
          ? onClearFilters
          : null
      }
    />
  )
}

FilteringChips.propTypes = {
  filters: PropTypes.object,
  disabledStatusCodeFilters: PropTypes.bool,
  onUpdateFilters: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
}

export default FilteringChips
